// M004-F0744 / F0746 (design sections 2, 3.2, 7): the receipt drawer - the one edit surface for a receipt, opened by a tile click. It edits
// merchant, date, total (+ currency), VAT, order / reference no. and the note. While the OCR status is `suggested` the empty fields show
// the suggestion, marked; NOTHING is committed until the user presses Confirm (`confirm_ocr: true`) or saves values they edited. `failed`
// and `unavailable` never block entry: every field stays editable and Save works. Every save reads its value back from the answer (P-4).
// A locked, deleted or offline envelope opens read-only. Refusals show the server's sentence and its code.
import React, { useEffect, useRef, useState } from 'react';
import useDismissOnOutsideClick from '../../hooks/useDismissOnOutsideClick';
import { envelopeApi, STILL_PROCESSING_CODE } from '../../services/envelopeApi';
import { classifyRelayOutcome } from '../../services/relayOutcome';
import {
  PROMOTE_CONFIRM_TEXT, captureBadge, extractEnvelopeError, formatAmount, formatBytes, formatDateTime, newIdempotencyKey,
  parseAmountInput, saveBlobDownload,
} from './envelopeModel';
import { confirmedAmount, receiptHeadline } from './envelopeWorkspaceModel';
import OcrChip from './OcrChip';
import {
  FORM_FIELDS, LOW_CONFIDENCE_TEXT, OCR_NOTES, buildBody, confirmedForm, confidenceText, initialForm, isLowConfidence, ocrStatusOf,
  suggestionConfidence, validateForm,
} from './ocrModel';
import { useReceiptImage } from './ReceiptThumb';
import { envelopesApi } from '../../services/api';
import useDialogFocus from './useDialogFocus';

export default function ReceiptDrawer({ orgId, envelopeId, receipt, readOnly = false, readOnlyReason = '', defaultCurrency = 'GBP', onClose, onSaved, onGone, onBusy = null, onReadBack = null }) {
  const docId = receipt.receipt_document_id;
  const ref = useRef(null);
  const dismiss = useDismissOnOutsideClick(onClose);
  useDialogFocus(ref, onClose, { initialFocus: `[data-testid="envelope-receipt-amount-${docId}"]` });
  const isPdf = receipt.content_type === 'application/pdf';
  const { src, failed } = useReceiptImage(!isPdf, orgId, envelopeId, docId);
  const [note, setNote] = useState(receipt.note || '');
  const first = useRef(initialForm(receipt));
  const [form, setForm] = useState(first.current.form);
  const [suggestedKeys, setSuggestedKeys] = useState(first.current.suggestedKeys);
  const [touched, setTouched] = useState(() => new Set());
  const [confirming, setConfirming] = useState('');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);
  const busyRef = useRef(null);
  busyRef.current = onBusy;
  useEffect(() => { // M004-F0793 d: the workspace does not refresh while a save is in flight
    const report = busyRef.current;
    if (typeof report === 'function') report(busy);
    return () => { if (typeof report === 'function' && busy) report(false); };
  }, [busy]);
  const [failure, setFailure] = useState(null);
  const [savedAt, setSavedAt] = useState('');
  const checked = validateForm(form, defaultCurrency);
  const invalid = !checked.amountOk;
  const headline = receiptHeadline(receipt);
  const ocrStatus = ocrStatusOf(receipt);
  const pending = ocrStatus === 'suggested' && !confirmedAmount(receipt);
  const confidence = suggestionConfidence(receipt);
  const noteChanged = note !== (receipt.note || '');
  const hasEdits = touched.size > 0 || noteChanged;

  // The read finishing while the drawer is open (reading -> suggested) fills the fields the user has not touched.
  const suggestionKey = `${ocrStatus}|${JSON.stringify(receipt.ocr?.suggestion || null)}`;
  useEffect(() => {
    const fresh = initialForm(receipt);
    setForm((current) => {
      const next = { ...current };
      FORM_FIELDS.forEach((key) => { if (!touched.has(key)) next[key] = fresh.form[key]; });
      return next;
    });
    setSuggestedKeys(fresh.suggestedKeys.filter((key) => !touched.has(key)));
  }, [suggestionKey]);

  const setField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setTouched((current) => new Set(current).add(key));
    setSuggestedKeys((current) => current.filter((item) => item !== key));
  };

  const fail = (cause) => { setFailure(extractEnvelopeError(cause)); setConfirming(''); };

  // `confirm` is true ONLY from the Confirm button. Save sends what the user edited; on a suggested receipt an untouched suggestion is not sent.
  const submit = async (confirm) => {
    if (busy || !checked.ok || readOnly) return;
    setBusy(true);
    setFailure(null);
    try {
      const body = buildBody({ form, touched, note, noteChanged: true, defaultCurrency, confirm, alwaysAmount: !pending });
      const response = await envelopeApi.receiptUpdate(orgId, envelopeId, docId, body);
      const saved = response?.data?.data || {};
      setNote(saved.note || '');
      setForm(confirmedForm(saved));
      setTouched(new Set());
      setSuggestedKeys([]);
      setSavedAt(confirm ? 'confirmed' : 'saved');
      onSaved(saved);
    } catch (cause) {
      fail(cause);
    } finally {
      setBusy(false);
    }
  };
  const save = () => submit(false);
  const confirm = () => submit(true);

  const retryRead = async () => {
    if (busy || readOnly) return;
    setBusy(true);
    setFailure(null);
    try {
      const response = await envelopeApi.receiptOcrRetry(orgId, envelopeId, docId);
      onSaved(response?.data?.data || {});
      setSavedAt('retry');
    } catch (cause) { fail(cause); } finally { setBusy(false); }
  };

  const downloadImage = async () => {
    try {
      const response = await envelopesApi.receiptImage(orgId, envelopeId, docId);
      saveBlobDownload(response.data, receipt.original_filename);
    } catch (cause) { fail(cause); }
  };

  const downloadJson = async () => {
    try {
      const response = await envelopeApi.receiptGet(orgId, envelopeId, docId);
      saveBlobDownload(new Blob([JSON.stringify(response?.data?.data, null, 2)], { type: 'application/json' }), `${receipt.original_filename}.json`);
    } catch (cause) { fail(cause); }
  };

  // #264: Remove waits for the Ledger's FINAL answer. A 202 pending replays the SAME key (envelopeApi); "Removing..." shows until then and
  // "removed" only after the final success. Retries exhausted / timeout / 5xx = unknown: re-read the envelope and show what is true. A
  // "not found" after a retry whose readback shows the receipt gone is our delete having landed.
  const remove = async () => {
    const key = newIdempotencyKey(); // once per Remove click
    let replayed = false;
    setBusy(true);
    setRemoving(true);
    setFailure(null);
    try {
      await envelopeApi.receiptDelete(orgId, envelopeId, docId, key, { onPending: () => { replayed = true; } });
      onGone(docId, 'removed');
    } catch (cause) {
      const info = extractEnvelopeError(cause);
      const unknown = info.code === STILL_PROCESSING_CODE || classifyRelayOutcome(cause) === 'unknown';
      const notFound = info.status === 404 && info.code === 'ENVELOPE_RECEIPT_NOT_FOUND';
      if (unknown || (replayed && notFound)) {
        const receipts = typeof onReadBack === 'function' ? await onReadBack() : null;
        if (Array.isArray(receipts) && !receipts.some((item) => item.receipt_document_id === docId)) onGone(docId, 'removed');
        else if (Array.isArray(receipts)) { setFailure({ code: '', message: 'The Ledger did not confirm the removal, and the re-read shows the receipt is still in this envelope. Nothing was removed.', status: 0 }); setConfirming(''); }
        else if (unknown) { setFailure({ code: info.code, message: 'The Ledger did not confirm the removal and the envelope could not be re-read. Refresh to see what is true.', status: info.status }); setConfirming(''); }
        else fail(cause);
      } else fail(cause);
    } finally { setBusy(false); setRemoving(false); }
  };

  const run = async (action, how) => {
    setBusy(true);
    setFailure(null);
    try {
      await action();
      onGone(docId, how);
    } catch (cause) { fail(cause); } finally { setBusy(false); }
  };

  return (
    <div className="ew-backdrop" role="presentation" {...dismiss}>
      <div
        ref={ref}
        className="ew-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ew-receipt-title"
        data-testid="envelope-receipt-drawer"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="ew-drawer-head">
          <div>
            <h2 id="ew-receipt-title">{headline.text}</h2>
            <p className="ew-muted">
              {formatDateTime(receipt.captured_at)}
              {' · '}
              {captureBadge(receipt.captured_at_origin)}
              {' · '}
              {formatBytes(receipt.size)}
            </p>
          </div>
          <button type="button" className="ew-btn" data-testid="envelope-receipt-close" onClick={onClose}>Close</button>
        </header>

        <div className="ew-drawer-image">
          {isPdf ? <span className="ew-thumb-file">PDF</span> : null}
          {!isPdf && src ? <img src={src} alt={`Receipt ${receipt.original_filename}`} /> : null}
          {!isPdf && !src && !failed ? <span className="ew-skel ew-image-skel" aria-hidden="true" /> : null}
          {!isPdf && failed ? <span className="ew-muted">Preview unavailable</span> : null}
        </div>

        {readOnly ? <p className="ew-notice" data-testid="envelope-receipt-readonly">{readOnlyReason || 'This receipt is read-only.'}</p> : null}
        {failure ? (
          <p className="ew-error" role="alert" data-testid="envelope-receipt-error">
            {failure.message}
            {failure.code ? <> <code data-testid="envelope-receipt-error-code">{failure.code}</code></> : null}
          </p>
        ) : null}

        <div className="ew-ocr" data-testid="envelope-receipt-ocr" data-ocr={ocrStatus || 'none'}>
          <OcrChip receipt={receipt} testId="envelope-receipt-ocr-chip" />
          {ocrStatus ? <p className="ew-muted ew-hint" data-testid="envelope-receipt-ocr-note">{OCR_NOTES[ocrStatus]}</p> : null}
          {pending && confidence !== null ? (
            <p className="ew-muted ew-hint" data-testid="envelope-receipt-ocr-confidence">
              {confidenceText(confidence)}
              {isLowConfidence(confidence) ? <>{' '}<span className="ocr-low-confidence">{LOW_CONFIDENCE_TEXT}</span></> : null}
            </p>
          ) : null}
        </div>

        <div className="ew-fields">
          {[
            ['merchant', 'Merchant', 'text'],
            ['receipt_date', 'Date', 'date'],
          ].map(([key, label, type]) => (
            <React.Fragment key={key}>
              <label htmlFor={`ew-${key}-${docId}`}>
                {label}
                {suggestedKeys.includes(key) ? <span className="ew-suggested-tag"> Suggested</span> : null}
              </label>
              <input
                id={`ew-${key}-${docId}`}
                type={type}
                value={form[key]}
                disabled={readOnly}
                aria-invalid={(key === 'receipt_date' && !checked.dateOk) || undefined}
                data-suggested={suggestedKeys.includes(key) ? 'true' : undefined}
                data-testid={`envelope-receipt-${key.replace('_', '-')}-${docId}`}
                onChange={(event) => setField(key, event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') save(); }}
              />
            </React.Fragment>
          ))}
          <label htmlFor={`ew-amount-${docId}`}>
            Amount
            {suggestedKeys.includes('amount') ? <span className="ew-suggested-tag"> Suggested</span> : null}
          </label>
          <input
            id={`ew-amount-${docId}`}
            value={form.amount}
            disabled={readOnly}
            placeholder={`12.50 ${defaultCurrency}`}
            aria-invalid={invalid || undefined}
            aria-describedby={`ew-amount-hint-${docId}`}
            data-suggested={suggestedKeys.includes('amount') ? 'true' : undefined}
            data-testid={`envelope-receipt-amount-${docId}`}
            onChange={(event) => setField('amount', event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') save(); }}
          />
          <p id={`ew-amount-hint-${docId}`} className="ew-muted ew-hint">
            {invalid ? 'Type an amount like 12.50 or 12.50 EUR.' : `Counts in the totals once saved. No currency means ${defaultCurrency}.`}
          </p>
          <label htmlFor={`ew-vat-${docId}`}>
            VAT
            {suggestedKeys.includes('vat') ? <span className="ew-suggested-tag"> Suggested</span> : null}
          </label>
          <input
            id={`ew-vat-${docId}`}
            value={form.vat}
            disabled={readOnly}
            inputMode="decimal"
            aria-invalid={!checked.vatOk || undefined}
            data-suggested={suggestedKeys.includes('vat') ? 'true' : undefined}
            data-testid={`envelope-receipt-vat-${docId}`}
            onChange={(event) => setField('vat', event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') save(); }}
          />
          <label htmlFor={`ew-reference-${docId}`}>
            Order / reference no.
            {suggestedKeys.includes('reference') ? <span className="ew-suggested-tag"> Suggested</span> : null}
          </label>
          <input
            id={`ew-reference-${docId}`}
            value={form.reference}
            disabled={readOnly}
            data-suggested={suggestedKeys.includes('reference') ? 'true' : undefined}
            data-testid={`envelope-receipt-reference-${docId}`}
            onChange={(event) => setField('reference', event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') save(); }}
          />
          <label htmlFor={`ew-note-${docId}`}>Note</label>
          <input
            id={`ew-note-${docId}`}
            value={note}
            disabled={readOnly}
            data-testid={`envelope-receipt-note-${docId}`}
            onChange={(event) => setNote(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') save(); }}
          />
        </div>

        <div className="ew-actions">
          {pending ? (
            <button type="button" className="ew-btn ew-btn-primary" disabled={readOnly || busy || !checked.ok || form.amount.trim() === ''} data-testid={`envelope-receipt-confirm-${docId}`} onClick={confirm}>Confirm</button>
          ) : null}
          <button type="button" className={`ew-btn${pending ? '' : ' ew-btn-primary'}`} disabled={readOnly || busy || !checked.ok || (pending && !hasEdits)} data-testid={`envelope-receipt-save-${docId}`} onClick={save}>Save</button>
          {ocrStatus === 'failed' ? (
            <button type="button" className="ew-btn" disabled={readOnly || busy} data-testid={`envelope-receipt-ocr-retry-${docId}`} onClick={retryRead}>Try again</button>
          ) : null}
          <button type="button" className="ew-btn" data-testid={`envelope-receipt-download-${docId}`} onClick={downloadImage}>Download</button>
          <button type="button" className="ew-btn" data-testid={`envelope-receipt-json-${docId}`} onClick={downloadJson}>JSON</button>
          {confirming === 'promote' ? (
            <span className="ew-confirm">
              {PROMOTE_CONFIRM_TEXT}
              <button type="button" className="ew-btn" disabled={busy} data-testid={`envelope-receipt-promote-confirm-${docId}`} onClick={() => run(() => envelopeApi.promote(orgId, envelopeId, docId, newIdempotencyKey()), 'promoted')}>Move it</button>
              <button type="button" className="ew-btn" onClick={() => setConfirming('')}>Keep it here</button>
            </span>
          ) : (
            <button type="button" className="ew-btn" disabled={readOnly || busy} data-testid={`envelope-receipt-promote-${docId}`} onClick={() => setConfirming('promote')}>Promote to receipt queue</button>
          )}
          {confirming === 'remove' ? (
            <span className="ew-confirm">
              Remove this receipt from the envelope?
              <button type="button" className="ew-btn ew-btn-danger" disabled={busy} data-testid={`envelope-receipt-remove-confirm-${docId}`} onClick={remove}>{removing ? 'Removing\u2026' : 'Remove'}</button>
              <button type="button" className="ew-btn" onClick={() => setConfirming('')}>Keep</button>
            </span>
          ) : (
            <button type="button" className="ew-btn ew-btn-danger" disabled={readOnly || busy} data-testid={`envelope-receipt-remove-${docId}`} onClick={() => setConfirming('remove')}>Remove</button>
          )}
        </div>
        <p className="ew-visually-hidden" role="status">{{ saved: 'Receipt saved', confirmed: 'Receipt confirmed', retry: 'Reading this receipt again' }[savedAt] || ''}</p>
      </div>
    </div>
  );
}
